import { useEffect, useState } from 'react'
import TenantModulesPanel from './TenantModulesPanel'
import { toast } from 'sonner'
import { platformService } from '@/services/platformService'
import type { Tenant, CreateTenantRequest, TenantUser } from '@/types/api.types'

const EMPTY_FORM: CreateTenantRequest = {
    slug: '', name: '', contactEmail: '', adminUsername: '', adminFullName: '', adminPassword: '',
}

const STATUS_STYLE: Record<string, string> = {
    ACTIVE: 'bg-emerald-900 text-emerald-300',
    TRIAL: 'bg-amber-900 text-amber-300',
    SUSPENDED: 'bg-rose-900 text-rose-300',
}

export default function TenantsManagementPage() {
    const [tenants, setTenants] = useState<Tenant[]>([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [form, setForm] = useState<CreateTenantRequest>(EMPTY_FORM)
    const [saving, setSaving] = useState(false)
    const [usersOf, setUsersOf] = useState<Tenant | null>(null)
    const [modulesOf, setModulesOf] = useState<Tenant | null>(null)
    const [users, setUsers] = useState<TenantUser[]>([])
    const [newUser, setNewUser] = useState({ username: '', fullName: '', email: '', password: '', role: 'OPERADOR' })

    const reload = () => {
        setLoading(true)
        platformService.listTenants().then(setTenants).catch(() => toast.error('No se pudo cargar tenants')).finally(() => setLoading(false))
    }
    useEffect(reload, [])

    const create = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaving(true)
        try {
            await platformService.createTenant(form)
            toast.success(`Tenant "${form.slug}" creado`)
            setShowModal(false)
            setForm(EMPTY_FORM)
            reload()
        } catch (err: any) {
            toast.error(err?.response?.data?.error || 'No se pudo crear el tenant')
        } finally {
            setSaving(false)
        }
    }

    const openUsers = async (t: Tenant) => {
        setUsersOf(t); setUsers([])
        try { setUsers(await platformService.listTenantUsers(t.slug)) }
        catch { toast.error('No se pudieron cargar los usuarios') }
    }
    const reloadUsers = async () => {
        if (!usersOf) return
        try { setUsers(await platformService.listTenantUsers(usersOf.slug)) } catch { /* noop */ }
    }
    const addUser = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!usersOf) return
        try {
            await platformService.createTenantUser(usersOf.slug, newUser)
            toast.success(`Usuario "${newUser.username}" creado`)
            setNewUser({ username: '', fullName: '', email: '', password: '', role: 'OPERADOR' })
            reloadUsers()
        } catch (err: any) { toast.error(err?.response?.data?.error || 'No se pudo crear el usuario') }
    }
    const toggleUser = async (u: TenantUser) => {
        if (!usersOf) return
        try { await platformService.setTenantUserActive(usersOf.slug, u.id, !u.active); reloadUsers() }
        catch { toast.error('No se pudo cambiar el estado') }
    }
    const resetUser = async (u: TenantUser) => {
        if (!usersOf) return
        const pwd = window.prompt(`Nueva contraseña temporal para ${u.username}:`)
        if (!pwd) return
        try { await platformService.resetTenantUserPassword(usersOf.slug, u.id, pwd); toast.success('Contraseña reseteada') }
        catch { toast.error('No se pudo resetear') }
    }

    const setStatus = async (t: Tenant, action: 'activate' | 'suspend') => {
        try {
            if (action === 'activate') await platformService.activateTenant(t.id)
            else await platformService.suspendTenant(t.id)
            toast.success(`Tenant ${action === 'activate' ? 'activado' : 'suspendido'}`)
            reload()
        } catch {
            toast.error('No se pudo cambiar el estado')
        }
    }

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Tenants</h1>
                <button onClick={() => setShowModal(true)}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">
                    + Nuevo tenant
                </button>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-800">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-800/70 text-slate-300">
                        <tr>
                            <th className="px-4 py-3">Nombre</th>
                            <th className="px-4 py-3">Slug</th>
                            <th className="px-4 py-3">Estado</th>
                            <th className="px-4 py-3">Contacto</th>
                            <th className="px-4 py-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {loading ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Cargando…</td></tr>
                        ) : tenants.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin tenants</td></tr>
                        ) : tenants.map((t) => (
                            <tr key={t.id} className="text-slate-200">
                                <td className="px-4 py-3">{t.name}</td>
                                <td className="px-4 py-3 font-mono text-slate-400">{t.slug}</td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[t.status] || 'bg-slate-700 text-slate-300'}`}>{t.status}</span>
                                </td>
                                <td className="px-4 py-3 text-slate-400">{t.contactEmail}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => openUsers(t)} className="mr-4 text-indigo-400 hover:underline">Usuarios</button>
                                    <button onClick={() => setModulesOf(t)} className="mr-4 text-indigo-400 hover:underline">Módulos</button>
                                    {t.status === 'SUSPENDED' ? (
                                        <button onClick={() => setStatus(t, 'activate')} className="text-emerald-400 hover:underline">Activar</button>
                                    ) : (
                                        <button onClick={() => setStatus(t, 'suspend')} className="text-rose-400 hover:underline">Suspender</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <form onSubmit={create} className="w-full max-w-md space-y-4 rounded-xl border border-slate-700 bg-slate-800 p-6">
                        <h2 className="text-lg font-semibold text-slate-100">Nuevo tenant</h2>
                        <div>
                            <label className="mb-1 block text-sm text-slate-300">Slug</label>
                            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                placeholder="ej. pdi-metropolitana" pattern="[a-z0-9-]{3,50}"
                                className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 font-mono text-slate-100 outline-none focus:border-indigo-500" required />
                            <p className="mt-1 text-xs text-slate-500">minúsculas, números y guiones (3-50)</p>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm text-slate-300">Nombre</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-indigo-500" required />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm text-slate-300">Email de contacto</label>
                            <input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                                className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-indigo-500" required />
                        </div>

                        <div className="border-t border-slate-700 pt-3">
                            <p className="mb-2 text-sm font-medium text-slate-300">Administrador inicial del tenant</p>
                            <div className="grid grid-cols-2 gap-3">
                                <input value={form.adminUsername} onChange={(e) => setForm({ ...form, adminUsername: e.target.value })}
                                    placeholder="usuario admin"
                                    className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-indigo-500" required />
                                <input value={form.adminFullName} onChange={(e) => setForm({ ...form, adminFullName: e.target.value })}
                                    placeholder="nombre completo"
                                    className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-indigo-500" />
                            </div>
                            <input type="password" value={form.adminPassword} onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
                                placeholder="contraseña temporal"
                                className="mt-3 w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-slate-100 outline-none focus:border-indigo-500" required />
                            <p className="mt-1 text-xs text-slate-500">Rol ADMINISTRADOR. Deberá cambiar la contraseña en el primer login.</p>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)}
                                className="rounded-md border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700">Cancelar</button>
                            <button type="submit" disabled={saving}
                                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60">
                                {saving ? 'Creando…' : 'Crear'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {usersOf && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <div className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-800 p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-slate-100">Usuarios · {usersOf.name}</h2>
                            <button onClick={() => setUsersOf(null)} className="text-slate-400 hover:text-slate-200">✕</button>
                        </div>
                        {users.length === 0 ? (
                            <p className="text-slate-400">Sin usuarios (o cargando…).</p>
                        ) : (
                            <ul className="max-h-64 divide-y divide-slate-700 overflow-auto">
                                {users.map((u) => (
                                    <li key={u.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                                        <div className="min-w-0">
                                            <div className="truncate text-slate-100">{u.fullName} <span className="text-slate-500">({u.username})</span></div>
                                            <div className="truncate text-xs text-slate-500">{u.email} · {u.roles.join(', ')}</div>
                                        </div>
                                        <div className="flex shrink-0 items-center gap-2">
                                            <button onClick={() => resetUser(u)} className="text-xs text-amber-400 hover:underline">reset pwd</button>
                                            <button onClick={() => toggleUser(u)} className={`rounded-full px-2 py-0.5 text-xs ${u.active ? 'bg-emerald-900 text-emerald-300' : 'bg-rose-900 text-rose-300'}`}>
                                                {u.active ? 'activo' : 'inactivo'}
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <form onSubmit={addUser} className="mt-4 space-y-2 border-t border-slate-700 pt-4">
                            <p className="text-sm font-medium text-slate-300">Nuevo usuario</p>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} placeholder="usuario" required
                                    className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                                <input value={newUser.fullName} onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })} placeholder="nombre"
                                    className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                                <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="email"
                                    className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                                <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                    className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500">
                                    <option>OPERADOR</option><option>SUPERVISOR</option><option>COORDINADOR</option><option>ADMINISTRADOR</option>
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="contraseña temporal" required
                                    className="flex-1 rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                                <button type="submit" className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">Crear</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {modulesOf && (
                <TenantModulesPanel slug={modulesOf.slug} name={modulesOf.name} onClose={() => setModulesOf(null)} />
            )}
        </div>
    )
}
